import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2, Clock, Send, FileSearch, Award, XCircle, Upload, ShieldAlert, Users, History } from "lucide-react";

const STATUS_DOT = {
  created: "bg-slate-400", submitted: "bg-blue-500",
  pending_access_validation: "bg-blue-500", in_review: "bg-amber-500",
  additional_info_required: "bg-orange-500", quarantined: "bg-red-500",
  processed: "bg-emerald-500",
};
import { formatCLP } from "@/lib/format";
import { toast } from "sonner";
import { categoryFeeUF } from "@/lib/categories";

const FLOW = ["Borrador", "Enviada", "En revisión", "Procesada"];

function Tracker({ current }) {
  const idx = FLOW.indexOf(current);
  const icons = [Send, Send, FileSearch, CheckCircle2];
  return (
    <div className="flex items-center justify-between bg-white rounded-2xl border border-border p-6">
      {FLOW.map((s, i) => {
        const Icon = icons[i];
        const done = idx >= i;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-2 flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${done ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
                <Icon className="w-5 h-5" />
              </div>
              <span className={`text-xs text-center ${done ? "text-primary font-semibold" : "text-muted-foreground"}`}>{s}</span>
            </div>
            {i < FLOW.length - 1 && <div className={`h-0.5 flex-1 ${idx > i ? "bg-primary" : "bg-secondary"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

const fmtUF = (n) => n.toLocaleString("es-CL", { maximumFractionDigits: 2 });

function FeeSummary({ category, pct }) {
  const fee = categoryFeeUF(category);
  if (fee == null || pct == null) return null;
  const payPct = Math.max(0, 100 - Number(pct));
  return (
    <div className="mt-5 mx-auto max-w-md bg-secondary/60 rounded-xl p-4 text-sm" data-testid="result-fee-summary">
      <p className="text-muted-foreground">Categoría <strong className="text-foreground">{category}</strong> · cuota anual <strong className="text-foreground">{fmtUF(fee)} UF</strong></p>
      <p className="mt-1 text-base">Cuota a pagar: <strong className="text-primary text-lg" data-testid="result-fee-to-pay">{fmtUF(fee * payPct / 100)} UF</strong> <span className="text-muted-foreground">({fmtUF(payPct)}%)</span></p>
    </div>
  );
}


export default function ApplicationResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [reconsidering, setReconsidering] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [recDocs, setRecDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/applications/${id}`);
      if (data.status === "draft") { navigate(`/solicitud/${id}`); return; }
      if (data.status === "additional_info_required") { navigate(`/solicitud/${id}/informacion`); return; }
      setApp(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); navigate("/panel"); }
  };
  useEffect(() => { load(); }, [id]);

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/applications/${id}/accept-result`, { action });
      toast.success(action === "accept" ? "Has aceptado el resultado." : "Has rechazado el resultado.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const uploadRecDoc = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", "reconsideracion");
      fd.append("scope", "reconsideration");
      const { data } = await api.post(`/applications/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRecDocs((prev) => [...prev, data.document]);
      toast.success("Documento adjuntado.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setUploading(false); }
  };

  const reconsider = async () => {
    if (explanation.trim().length < 10) { toast.error("Debes aportar información nueva (mínimo 10 caracteres)."); return; }
    if (recDocs.length < 1) { toast.error("Debes adjuntar al menos un documento nuevo de respaldo."); return; }
    setBusy(true);
    try {
      await api.post(`/applications/${id}/reconsider`, { explanation });
      toast.success("Reevaluación solicitada. Tu solicitud volvió a revisión.");
      setReconsidering(false); setRecDocs([]); setExplanation(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!app) return <AppShell><div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AppShell>;

  const decision = app.decision;
  const finalPct = app.final_pct;
  const isProcessed = app.status === "processed";
  const requested = app.requested_pct;
  const exactMatch = app.accept_committee_pct || (finalPct != null && requested != null && finalPct === requested);
  const resolved = app.resolution;

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{app.human_id || "Solicitud"}</h1>
          <p className="text-muted-foreground">Estado de tu solicitud</p>
        </div>
        <StatusBadge status={app.public_status} testId="result-status" />
      </div>

      <Tracker current={app.public_status === "Reevaluación solicitada" ? "En revisión" : app.public_status} />

      {app.timeline?.length > 0 && (
        <div className="mt-6 bg-white rounded-2xl border border-border p-6" data-testid="application-timeline">
          <p className="flex items-center gap-2 font-semibold text-primary mb-4"><History className="w-5 h-5 text-accent" /> Historial de tu solicitud</p>
          <ol className="relative border-l-2 border-secondary ml-2">
            {app.timeline.map((t, i) => (
              <li key={i} className="ml-5 pb-5 last:pb-0" data-testid={`timeline-item-${i}`}>
                <span className={`absolute -left-[7px] w-3 h-3 rounded-full ${STATUS_DOT[t.status] || "bg-slate-300"}`} />
                <p className="text-sm font-semibold text-foreground">{t.label}</p>
                <p className="text-xs text-muted-foreground">{t.at ? new Date(t.at).toLocaleString("es-CL", { dateStyle: "medium", timeStyle: "short" }) : "—"}</p>
              </li>
            ))}
          </ol>
        </div>
      )}

      {app.status === "quarantined" && app.quarantine_reason === "ingreso_no_aprobado" && (
        <div className="mt-6 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 flex items-start gap-3" data-testid="recommenders-needed-notice">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">Tu ingreso no fue aceptado</p>
            <p className="text-sm text-amber-800 mb-3">Para continuar, indica el nombre, RUT y correo de 3 socios del EIM que te recomienden. EIM revisará tu ingreso nuevamente.</p>
            <Button onClick={() => navigate(`/solicitud/${id}/recomendaciones`)} className="btn-lift h-10 rounded-xl" data-testid="btn-go-recommenders">Ingresar 3 socios recomendantes</Button>
          </div>
        </div>
      )}

      {app.status === "quarantined" && app.quarantine_reason === "ingreso_rechazado_final" && (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5 flex items-start gap-3" data-testid="quarantine-final-notice">
          <XCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-red-900">Tu proceso no fue aceptado</p>
            <p className="text-sm text-red-800">Tras revisar a tus socios recomendantes, tu ingreso no fue aceptado. Si tienes dudas, escríbenos a becas@eimbecas.cl.</p>
          </div>
        </div>
      )}

      {app.status === "pending_access_validation" && (app.recommenders?.length > 0) && (
        <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 p-5 flex items-start gap-3" data-testid="recommenders-submitted-notice">
          <Users className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-blue-900">Recibimos tus recomendaciones</p>
            <p className="text-sm text-blue-800">EIM está revisando nuevamente tu ingreso con los 3 socios que indicaste. Te avisaremos por correo con la decisión.</p>
            {app.recommender_confirmations?.length > 0 && (
              <ul className="mt-3 space-y-1" data-testid="recommender-confirmations">
                {app.recommender_confirmations.map((c, i) => {
                  const st = c.status === "confirmed" ? { t: "Confirmó", cls: "text-emerald-700" }
                    : c.status === "declined" ? { t: "No recomendó", cls: "text-slate-500" }
                    : { t: "Pendiente", cls: "text-amber-700" };
                  return <li key={i} className="text-sm flex items-center justify-between gap-2"><span className="text-blue-900">{c.recommender_name}</span><span className={`text-xs font-semibold ${st.cls}`}>{st.t}</span></li>;
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {app.status === "pending_access_validation" && !(app.recommenders?.length > 0) && (
        <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 p-5 flex items-start gap-3" data-testid="access-pending-notice">
          <Clock className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-blue-900">Tu solicitud fue enviada a validación</p>
            <p className="text-sm text-blue-800">Como aún no estás en nuestra base de socios, revisaremos tu solicitud antes de continuar. Te avisaremos por correo apenas sea validada.</p>
          </div>
        </div>
      )}

      {app.status === "in_review" && app.access_granted && (
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3" data-testid="access-granted-notice">
          <Clock className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-emerald-900">Puedes usar las instalaciones mientras te evaluamos</p>
            <p className="text-sm text-emerald-800">Mientras dura tu evaluación podrás hacer uso de las instalaciones como cualquier socio. Te avisaremos por correo cuando tu solicitud esté procesada.</p>
          </div>
        </div>
      )}

      {app.ih_scholarship_status === "in_review" && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3" data-testid="ih-inreview-notice">
          <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-amber-900">Tu beca del Instituto Hebreo está en revisión</p>
            <p className="text-sm text-amber-800 mt-1">La asistente social del Instituto Hebreo está revisando tu caso. Te avisaremos cuando esté definido. No necesitas hacer nada por ahora.</p>
          </div>
        </div>
      )}

      {!isProcessed && (
        <div className="bg-white rounded-2xl border border-border p-8 mt-6 text-center">
          <Clock className="w-10 h-10 text-accent mx-auto mb-3" />
          <h2 className="text-xl font-semibold mb-2">Tu solicitud está en revisión</h2>
          <p className="text-muted-foreground max-w-md mx-auto">La evaluaremos y te avisaremos por email cuando esté procesada. No necesitas hacer nada más por ahora.</p>
        </div>
      )}

      {isProcessed && decision?.decision === "reject" && (
        <div className="bg-white rounded-2xl border border-border p-8 mt-6 text-center" data-testid="result-rejected">
          <XCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
          <h2 className="text-xl font-semibold mb-2">Tu solicitud no fue aprobada</h2>
          <p className="text-muted-foreground max-w-md mx-auto">{decision.comment}</p>
        </div>
      )}

      {isProcessed && decision && decision.decision !== "reject" && (
        <div className="bg-white rounded-2xl border border-border p-8 mt-6" data-testid="result-approved">
          <div className="text-center mb-6">
            <Award className="w-10 h-10 text-accent mx-auto mb-3" />
            <h2 className="text-xl font-semibold">EIM ha aprobado una reducción de</h2>
            <div className="text-6xl font-black text-primary my-3">{finalPct}%</div>
            {requested != null && <p className="text-sm text-muted-foreground">Solicitaste: {requested}%</p>}
            <FeeSummary category={app.family?.titular?.categoria} pct={finalPct} />
          </div>

          {resolved ? (
            <div className="text-center bg-secondary/60 rounded-xl p-4">
              <p className="font-semibold text-primary">{resolved === "accept" ? "Has aceptado este resultado." : "Has rechazado este resultado."}</p>
            </div>
          ) : exactMatch ? (
            <div className="text-center">
              <p className="text-muted-foreground mb-4">Se aprobó lo solicitado.</p>
              <Button onClick={() => act("accept")} disabled={busy} className="btn-lift h-12 px-8 rounded-xl" data-testid="btn-accept-result">Confirmar y aceptar</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {decision?.comment && (
                <div className="bg-secondary/60 rounded-xl p-4" data-testid="result-decision-comment">
                  <p className="text-sm font-semibold text-primary mb-1">Fundamento de la comisión</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{decision.comment}</p>
                </div>
              )}
              <p className="text-center text-muted-foreground">EIM ofrece un porcentaje distinto al solicitado. ¿Qué deseas hacer?</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <Button onClick={() => act("accept")} disabled={busy} className="btn-lift h-12 rounded-xl" data-testid="btn-accept-result">Aceptar</Button>
                <Button onClick={() => act("reject")} disabled={busy} variant="outline" className="h-12 rounded-xl border-2" data-testid="btn-reject-result">Rechazar</Button>
                <Button onClick={() => setReconsidering(true)} variant="outline" className="h-12 rounded-xl border-2" data-testid="btn-reconsider">Solicitar reevaluación</Button>
              </div>
            </div>
          )}

          {reconsidering && (
            <div className="mt-6 border-t border-border pt-6">
              <p className="text-sm mb-3">Para solicitar una reevaluación debes aportar <strong>información nueva y documentación de respaldo</strong> que pueda modificar la evaluación anterior.</p>
              <Textarea className="rounded-xl" placeholder="Explica los nuevos antecedentes…" value={explanation} onChange={(e) => setExplanation(e.target.value)} data-testid="reconsider-explanation" />
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Documentos nuevos de respaldo</p>
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl p-4 cursor-pointer hover:bg-secondary/40 text-sm text-muted-foreground" data-testid="reconsider-upload-label">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Subiendo…" : "Adjuntar documento (PDF, JPG o PNG)"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={uploadRecDoc} disabled={uploading} data-testid="reconsider-file-input" />
                </label>
                {recDocs.length > 0 && (
                  <ul className="mt-3 space-y-1" data-testid="reconsider-doc-list">
                    {recDocs.map((d, i) => (
                      <li key={i} className="text-sm flex items-center gap-2 text-emerald-700"><CheckCircle2 className="w-4 h-4" /> {d.original_filename}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={() => { setReconsidering(false); }} className="rounded-xl border-2">Cancelar</Button>
                <Button onClick={reconsider} disabled={busy} className="btn-lift rounded-xl flex-1" data-testid="btn-submit-reconsider">Enviar reevaluación</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
